import { get } from './index';

/**
 *
 * @param {number} orgId
 * @param {string} type
 * @returns {Promise<Object>} information about the export, including export file id and filename
 */
export const getExport = (orgId, type) => {
  return get(`/api/v1/exports?organization_id=${orgId}&type=${type}`);
};

/**
 *
 * @param {number} orgId
 * @param {number} seasonId
 * @returns {Promise<Object>} information about the export, including export file id and filename
 */

export const getSeasonExport = (orgId, seasonId) => {
  return get(`/api/v1/exports?organization_id=${orgId}&type=seasons&season_id=${seasonId}`);
};

/**
 *
 * @param {*} interval
 * @returns {Promise<void>} delay function that returns a promise that resolves after the interval
 */
const delay = (interval) => new Promise((resolve) => setTimeout(resolve, interval));
/**
 * @param {Object} params - The parameters for the function
 * we need to pass necessary parameters to the function as an object
 * example: getFile({ exportId: 1, organizationId: 1, filename: 'file.csv})
 * @param {number} params.exportId - Required the export id
 * @param {number} params.organizationId - Required the organization id
 * @param {string} params.filename - Required the name of the file to download
 * @param {function} [params.showToast] - Optional function to show a toast message
 * @param {function} [params.hideToast] -  Optional function set the duration of the toast to 0 and hide it
 * @param {string} [params.toastId] - Optional id of the toast
 * @param {string} [params.type] - Optional type of export for toast message
 * @param {string} [params.errorMessage] - Optional error message to display if the export fails
 * @param {string} [params.seasonName] - Optional name of the season for the export
 * @param {number} [params.interval=2000] - Optional interval to check if the export is ready
 * @returns {Promise<void>} functionality to download the file
 *
 *
 * function getFile is a function that fetches the export file from the server
 * and returns a promise that resolves when the file is ready to download
 * with toast messages to inform the user of the progress
 *
 * arguments are passed to the function as an object to make them optional
 * the poll will not run without exportId and organizationId
 * the filename is required to download the file
 * showToast and hideToast are optional functions to show and hide toast messages
 * toastId is the id of the toast message and optional
 * type is the type of export and optional
 * errorMessage is the message to display if the export fails and optional
 * interval is the time to wait before checking if the export is ready and optional
 */

export const getFile = ({
  exportId, organizationId, filename, showToast, hideToast, toastId, type, errorMessage, seasonName, interval = 2000,
}) => {
  const runPoll = () => {
    return get(`/api/v1/downloads/${exportId}?organization_id=${organizationId}`)
      .then((response) => {
      // status 304 means the resource has not been modified - still waiting for the export to finish
        if (response.status === 304) {
          return delay(interval).then(() => runPoll());
        }
        // status 200 means the export is ready to download
        if (response.status === 200) {
          // hide the preparing export toast opened before running the getFile function
          if (hideToast && toastId) {
            hideToast(toastId);
          }
          // file is ready to download
          return response.blob()
            .then((blob) => {
              if (!blob) {
                throw new Error(errorMessage || 'No file found');
              }
              // create a link to download the file
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename; // Set the file name here
              document.body.appendChild(link);
              link.click();
              // after the file is downloaded, remove the link and revoke the object URL
              window.URL.revokeObjectURL(url);
              link.remove();
              // show a success toast if showToast is provided
              if (showToast && type) {
                showToast({ message: `${seasonName || type} export is complete`, type: 'success' });
              }
            });
        }
        // if the status is not 304 or 200, throw an error
        throw new Error(response.statusText || errorMessage);
      })
      .catch((error) => {
        // hide the preparing export toast opened before running the getFile function
        if (hideToast && toastId) {
          hideToast(toastId);
        }
        // show an error toast if showToast is provided
        if (showToast) {
          showToast({ message: error?.message || errorMessage, type: 'error' });
        }
      });
  };
  return runPoll();
};
